import React, { useEffect, useRef, useState } from "react";
import pen from "../../../../assets/icons/pen.png";
import { CoinsIcon } from "lucide-react";
import getIcon from "../../../../utils/Icon";
import { useDispatch, useSelector } from "react-redux";
import { updatePromoFields } from "../../../../store/slices/promoFormSlice";
import TokenApi from "../../../../api/tokenApi";
import { message } from "antd";

// Блокчейн-иконки
import ethereum from "../../../../assets/icons/blockchains/ETH.png";
import bsc from "../../../../assets/icons/blockchains/BNB.png";
import solana from "../../../../assets/icons/blockchains/solana.png";
import base from "../../../../assets/icons/blockchains/base.svg";
import sui from "../../../../assets/icons/blockchains/sui.svg";
import ton from "../../../../assets/icons/blockchains/TON.png";
import polygon from "../../../../assets/icons/blockchains/polygon.svg";
import optimism from "../../../../assets/icons/blockchains/optimism.svg";
import arbitrum from "../../../../assets/icons/blockchains/arbitrum.svg";
import avalanche from "../../../../assets/icons/blockchains/avalanche.svg";

export default function PromoBudget() {
    const dispatch = useDispatch();
    const promoForm = useSelector((s) => s.promoForm);
    const [editMode, setEditMode] = useState(null);       // 'count' | 'token'
    const [editableValue, setEditableValue] = useState("");
    const [lookupNeeded, setLookupNeeded] = useState(false);
    const [pendingAddress, setPendingAddress] = useState("");
    const inputRef = useRef(null);

    const numbers = [1, 3, 5, 7, 10, 20, 50];

    const supportedChains = [
        { name: "ethereum", text: "ETH" },
        { name: "bsc", text: "BNB" },
        { name: "solana", text: "Solana" },
        { name: "sui", text: "SUI" },
        { name: "base", text: "Base" },
        { name: "ton", text: "TON" },
        { name: "polygon", text: "Polygon" },
        { name: "optimism", text: "Optimism" },
        { name: "arbitrum", text: "Arbitrum" },
        { name: "avalanche", text: "Avalanche" },
    ];

    const chainIcons = {
        ethereum,
        bsc,
        solana,
        base,
        sui,
        ton,
        polygon,
        optimism,
        arbitrum,
        avalanche,
    };

    const [selectedChain, setSelectedChain] = useState(
        supportedChains.find((c) => c.name === promoForm.tokenType) ||
        supportedChains[0]
    );

    // Закрываем editMode при клике вне
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (inputRef.current && !inputRef.current.contains(e.target)) {
                setEditMode(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const applyTokenData = (tokenData) => {
        const found = supportedChains.find((c) => c.name === tokenData.blockchain);
        if (found) setSelectedChain(found);

        const logoUrl = tokenData.logoUrl
            || (tokenData.logoPath ? `https://api.duotetra.quest/${tokenData.logoPath}` : null);

        dispatch(
            updatePromoFields({
                tokenAddress: tokenData.address,
                tokenType: tokenData.address,
                tokenLogoUrl: logoUrl,
                tokenSymbol: tokenData.symbol || null,
            })
        );
        const sym = tokenData.symbol ? tokenData.symbol.toUpperCase() : '???';
        message.success(`Токен загружен: ${tokenData.name || tokenData.address} (${sym})`);
    };

    const handleTokenAddressLookup = async (address) => {
        setLookupNeeded(false);
        setPendingAddress("");

        try {
            const raw = await TokenApi.getTokenByAddress(address);
            const tokenData = raw?.data || raw;
            if (tokenData && (tokenData.symbol || tokenData.name)) {
                applyTokenData(tokenData);
                return;
            }
        } catch (_) {}

        message.info("Токен не найден в базе. Выберите сеть для расширенного поиска.");
        setLookupNeeded(true);
        setPendingAddress(address);
    };

    const handleChainLookup = async (address, chainName) => {
        try {
            const raw = await TokenApi.lookupToken(address, chainName.toLowerCase());
            const lookupData = raw?.data || raw;

            if (!lookupData || (!lookupData.symbol && !lookupData.name)) {
                message.warning("Внешний поиск не дал результатов.");
                return;
            }

            if (lookupData.symbol) {
                applyTokenData(lookupData);
                return;
            }

            const refetch = await TokenApi.getTokenByAddress(address);
            const tokenData = refetch?.data || refetch;
            if (tokenData && tokenData.symbol) {
                applyTokenData(tokenData);
            } else {
                message.warning("Токен не удалось загрузить после lookup.");
            }
        } catch (err) {
            console.error("Chain lookup error:", err?.response?.data || err?.message || err);
            message.error("Ошибка при расширенном поиске. Проверьте консоль для деталей.");
        } finally {
            setLookupNeeded(false);
            setPendingAddress("");
        }
    };

    return (
        <div className="flex w-full flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-xl">
            <div className="flex w-full flex-row pl-2">
                <div className="rounded-lg border border-white/10 bg-white/[0.08] px-2 py-0.5 text-sm font-medium text-white/90">
                    1 Prize pool
                </div>
            </div>

            {/* Кнопки */}
            <div className="flex flex-row items-center justify-center gap-2">
                <div
                    onClick={() => {
                        setEditMode("count");
                        setEditableValue("");
                        setLookupNeeded(false);
                    }}
                    className="flex w-fit cursor-pointer flex-row items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1 transition hover:border-white/20 hover:bg-white/10"
                >
                    Custom count <img src={pen} className="w-4 h-4" alt="" />
                </div>
                <div
                    onClick={() => {
                        setEditMode("token");
                        setEditableValue("");
                        setLookupNeeded(false);
                    }}
                    className="flex w-fit cursor-pointer flex-row items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1 transition hover:border-white/20 hover:bg-white/10"
                >
                    Token address <CoinsIcon className="w-4 h-4" />
                </div>
            </div>

            {/* Быстрые числа */}
            <div className="w-full flex flex-row items-center justify-center gap-2 overflow-x-auto scrollbar-hide">
                {numbers.map((v) => (
                    <div
                        key={v}
                        onClick={() => dispatch(updatePromoFields({ totalAmount: v }))}
                        className={`min-w-[70px] h-[30px] flex items-center justify-center rounded-md cursor-pointer border ${
                            promoForm.totalAmount === v
                                ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200"
                                : "border-white/10 text-white/85 hover:border-white/20 hover:bg-white/10"
                        } text-sm`}
                    >
                        {v}
                    </div>
                ))}
            </div>

            {/* Поле ввода */}
            {editMode && (
                <div
                    ref={inputRef}
                    contentEditable
                    suppressContentEditableWarning
                    onFocus={(e) => (e.currentTarget.innerText = "")}
                    onInput={(e) => {
                        const t = e.currentTarget.innerText.trim();
                        if (editMode === "count") {
                            /^\d+$/.test(t) && setEditableValue(t);
                        } else {
                            setEditableValue(t);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            e.currentTarget.blur();
                        }
                    }}
                    onBlur={async () => {
                        if (editMode === "count") {
                            const num = parseInt(editableValue);
                            if (!isNaN(num) && num > 0) {
                                dispatch(updatePromoFields({ totalAmount: num }));
                            }
                        }
                        if (editMode === "token" && editableValue) {
                            await handleTokenAddressLookup(editableValue);
                        }
                        setEditMode(null);
                    }}
                    className="h-[35px] w-full rounded-lg border border-white/10 bg-white/[0.06] px-2 py-1 text-sm text-white outline-none focus:border-emerald-400/40"
                    style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                    {editMode === "count" ? "Enter number" : "Enter token address"}
                </div>
            )}

            {/* Список сетей */}
            <div
                className={`w-full flex flex-wrap items-center justify-center gap-2 ${
                    lookupNeeded
                        ? "rounded-md bg-amber-500/10 p-2 ring-2 ring-amber-400/40"
                        : ""
                }`}
            >
                {supportedChains.map((chain) => {
                    const isSelected = chain.name === selectedChain.name;
                    return (
                        <div
                            key={chain.name}
                            onClick={async () => {
                                if (lookupNeeded && pendingAddress) {
                                    await handleChainLookup(pendingAddress, chain.name);
                                } else {
                                    dispatch(updatePromoFields({ tokenType: chain.name }));
                                    setSelectedChain(chain);
                                }
                            }}
                            className={`cursor-pointer text-white border rounded-md flex items-center justify-center w-[60px] h-[35px] gap-1 bg-fill
                ${isSelected ? "border-emerald-400/60" : "border-white/10 grayscale"}`}
                        >
                            <img
                                src={chainIcons[chain.name]}
                                className="w-4 h-4"
                                alt={chain.name}
                            />
                            <p
                                className={`text-[9px] font-semibold truncate ${
                                    isSelected ? "text-white" : "text-accent_text"
                                }`}
                            >
                                {chain.text}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
